import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { sb } from '../../lib/supabase';
import { useSupabaseAuth } from '../../context/AuthContext';
import useWorkspaceAccess from '../../hooks/useWorkspaceAccess';
import Loader from '../../components/Loader';
import InitiationLayout from '../../components/InitiationLayout';
import Badge from '../../components/Badge';
import {
  OPTION_STATUS_LABEL,
  OPTION_WORKSPACE_TABS,
  fetchOptionDetail,
  fetchOptionAssessments,
  upsertAssessment,
  fitGapSummary,
  fetchOptionCostData,
  fetchOptionRomSnapshot,
  ensureBusinessCaseForOption,
  refreshBusinessCaseTotals,
} from '../../lib/solutionOptions';
import {
  OOTB_CLASSIFICATION_LABEL,
  T_SHIRT_SIZES,
  ARCHITECTURE_NARRATIVE_SECTIONS,
  INTERFACE_TYPE_LABEL,
  defaultArchitectureNarrative,
  defaultIntegrationShape,
} from '../../lib/initiation';
import {
  COST_CATEGORY_LABEL,
  BILLING_FREQUENCY_LABEL,
  CAPEX_OPEX_LABEL,
  calcOptionTCO,
  formatCurrency,
} from '../../lib/costEngine';

function WorkspaceTabs({ active, onChange, multiPlatform }) {
  return (
    <div className="sa-tab-nav">
      {OPTION_WORKSPACE_TABS.filter((t) => t.key !== 'integrations' || multiPlatform).map((t) => (
        <button
          key={t.key}
          type="button"
          className={`sa-tab-item${active === t.key ? ' on' : ''}`}
          onClick={() => onChange(t.key)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export default function SolutionOptionDetail() {
  const { optionId } = useParams();
  const { project, programme } = useOutletContext();
  const navigate = useNavigate();
  const { profile } = useSupabaseAuth();
  const { canWrite } = useWorkspaceAccess();

  const [option, setOption] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [assessments, setAssessments] = useState(new Map());
  const [candidates, setCandidates] = useState([]);
  const [interfaces, setInterfaces] = useState([]);
  const [doc, setDoc] = useState(null);
  const [costTypes, setCostTypes] = useState([]);
  const [costData, setCostData] = useState(null);
  const [romCalc, setRomCalc] = useState(null);
  const [activeTab, setActiveTab] = useState('platforms');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [platformInput, setPlatformInput] = useState('');
  const [newInterface, setNewInterface] = useState({ name: '', source_system: '', target_system: '', interface_type: 'batch' });
  const [showAddInterface, setShowAddInterface] = useState(false);
  const [costModal, setCostModal] = useState(null);

  const showToast = (msg, type = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const platforms = useMemo(
    () => (Array.isArray(option?.platforms_json) ? option.platforms_json : []),
    [option?.platforms_json]
  );
  const multiPlatform = platforms.length > 1;
  const locked = option?.status === 'selected' || option?.status === 'rejected';
  const readOnly = !canWrite || locked;
  const narrative = useMemo(
    () => ({ ...defaultArchitectureNarrative(), ...(doc?.narrative_json || {}) }),
    [doc?.narrative_json]
  );

  const load = useCallback(async () => {
    if (!optionId || !project?.id) return;
    setLoading(true);
    try {
      const opt = await fetchOptionDetail(optionId);
      if (!opt) {
        showToast('Option not found');
        navigate(`/project/${project.id}/initiation/solution-options`);
        return;
      }
      setOption(opt);
      setPlatformInput((Array.isArray(opt.platforms_json) ? opt.platforms_json : []).join(', '));

      const { requirements: reqs, assessments: assessMap, candidates: cands } =
        await fetchOptionAssessments(optionId, project.id);
      setRequirements(reqs);
      setAssessments(assessMap);
      setCandidates(cands);

      const [ifaceRes, docRes, typesRes, costs, rom] = await Promise.all([
        sb.from('interfaces').select('*').eq('architecture_option_id', optionId).order('ref'),
        sb.from('solution_architecture_documents')
          .select('*')
          .eq('architecture_option_id', optionId)
          .maybeSingle(),
        sb.from('cost_types').select('*').eq('is_active', true).order('name'),
        fetchOptionCostData(optionId, project.id, programme?.id),
        fetchOptionRomSnapshot(optionId, project.id, programme?.id),
      ]);

      setInterfaces(ifaceRes.data || []);
      setDoc(docRes.data);
      setCostTypes(typesRes.data || []);
      setCostData(costs);
      setRomCalc(rom);

      if (programme?.id) {
        const bcId = await ensureBusinessCaseForOption(optionId, project.id, programme.id, profile?.id);
        if (bcId && (costs.recurringLines.length || costs.oneTimeLines.length)) {
          await refreshBusinessCaseTotals(bcId);
        }
      }
    } catch (e) {
      console.error('[SolutionOptionDetail] load failed', e);
      showToast(e.message || 'Failed to load option');
    } finally {
      setLoading(false);
    }
  }, [optionId, project?.id, programme?.id, navigate, profile?.id]);

  useEffect(() => { load(); }, [load]);

  const summary = useMemo(() => fitGapSummary(assessments, requirements), [assessments, requirements]);

  async function savePlatforms() {
    const list = platformInput.split(',').map((s) => s.trim()).filter(Boolean);
    setSaving(true);
    try {
      const { error } = await sb.from('architecture_options').update({
        platforms_json: list,
        status: option.status === 'draft' ? 'in_assessment' : option.status,
      }).eq('id', optionId);
      if (error) throw error;
      await load();
      showToast('Platforms saved', 'success');
    } catch (e) {
      showToast(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function updateAssessment(reqId, patch) {
    setSaving(true);
    try {
      await upsertAssessment(optionId, reqId, patch, profile?.id);
      setAssessments((prev) => {
        const next = new Map(prev);
        next.set(reqId, { ...(next.get(reqId) || {}), requirement_id: reqId, architecture_option_id: optionId, ...patch });
        return next;
      });
    } catch (e) {
      showToast(e.message || 'Assessment update failed');
    } finally {
      setSaving(false);
    }
  }

  async function addInterface() {
    if (!newInterface.name.trim()) return;
    setSaving(true);
    try {
      const ref = `IF-${String(interfaces.length + 1).padStart(3, '0')}`;
      const { error } = await sb.from('interfaces').insert({
        project_id: project.id,
        architecture_option_id: optionId,
        ref,
        name: newInterface.name.trim(),
        source_system: newInterface.source_system.trim() || 'TBD',
        target_system: newInterface.target_system.trim() || 'TBD',
        interface_type: newInterface.interface_type,
        status: 'draft',
      });
      if (error) throw error;
      setShowAddInterface(false);
      setNewInterface({ name: '', source_system: '', target_system: '', interface_type: 'batch' });
      await load();
      showToast('Interface added', 'success');
    } catch (e) {
      showToast(e.message || 'Failed to add interface');
    } finally {
      setSaving(false);
    }
  }

  async function ensureDoc() {
    if (doc?.id) return doc.id;
    const { data, error } = await sb.from('solution_architecture_documents').insert({
      programme_id: programme.id,
      project_id: project.id,
      architecture_option_id: optionId,
      status: 'draft',
      narrative_json: defaultArchitectureNarrative(),
      integration_shape_json: defaultIntegrationShape(),
    }).select('id').single();
    if (error) throw error;
    return data.id;
  }

  async function saveNarrative(key, value) {
    setSaving(true);
    try {
      const docId = await ensureDoc();
      const next = { ...narrative, [key]: value };
      const { error } = await sb.from('solution_architecture_documents')
        .update({ narrative_json: next, status: 'draft', updated_by: profile?.id })
        .eq('id', docId);
      if (error) throw error;
      setDoc((d) => ({ ...d, id: docId, narrative_json: next, status: 'draft' }));
    } catch (e) {
      showToast(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function saveCostLine(form) {
    setSaving(true);
    try {
      const ct = costTypes.find((t) => t.id === form.cost_type_id) || costTypes[0];
      if (costModal.kind === 'recurring') {
        const payload = {
          architecture_option_id: optionId,
          cost_type_id: form.cost_type_id,
          description: form.description || null,
          quantity: Number(form.quantity) || 1,
          unit_rate: Number(form.unit_rate) || 0,
          billing_frequency: form.billing_frequency,
          start_year: Number(form.start_year) || 1,
          end_year: Number(form.end_year) || 5,
          escalation_pct: form.escalation_pct !== '' ? Number(form.escalation_pct) : null,
          currency_code: form.currency_code || 'GBP',
        };
        if (form.id) {
          await sb.from('recurring_cost_lines').update(payload).eq('id', form.id);
        } else {
          await sb.from('recurring_cost_lines').insert({
            ...payload,
            unit_rate: payload.unit_rate || ct?.default_rate || 0,
          });
        }
      } else {
        const payload = {
          architecture_option_id: optionId,
          cost_type_id: form.cost_type_id,
          description: form.description || null,
          amount: Number(form.amount) || 0,
          capex_opex: form.capex_opex || 'opex',
          currency_code: form.currency_code || 'GBP',
        };
        if (form.id) {
          await sb.from('one_time_cost_lines').update(payload).eq('id', form.id);
        } else {
          await sb.from('one_time_cost_lines').insert({
            ...payload,
            amount: payload.amount || ct?.default_rate || 0,
          });
        }
      }
      const bcId = await ensureBusinessCaseForOption(optionId, project.id, programme.id, profile?.id);
      await refreshBusinessCaseTotals(bcId);
      setCostModal(null);
      await load();
      showToast('Cost line saved', 'success');
    } catch (e) {
      showToast(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function deleteCostLine(kind, id) {
    setSaving(true);
    try {
      const table = kind === 'recurring' ? 'recurring_cost_lines' : 'one_time_cost_lines';
      await sb.from(table).delete().eq('id', id);
      const bcId = costData?.businessCase?.id
        || await ensureBusinessCaseForOption(optionId, project.id, programme.id, profile?.id);
      if (bcId) await refreshBusinessCaseTotals(bcId);
      await load();
      showToast('Cost line removed', 'success');
    } catch (e) {
      showToast(e.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  }

  function openCostModal(kind, line = null) {
    const defaultType = costTypes[0];
    if (!defaultType && !line) {
      showToast('No cost types configured — add them in Admin');
      return;
    }
    setCostModal({
      kind,
      form: line ? { ...line } : {
        cost_type_id: defaultType?.id,
        description: '',
        quantity: 1,
        unit_rate: defaultType?.default_rate || 0,
        billing_frequency: defaultType?.default_billing_frequency || 'annual',
        start_year: 1,
        end_year: 5,
        escalation_pct: '',
        amount: defaultType?.default_rate || 0,
        capex_opex: 'opex',
        currency_code: 'GBP',
      },
    });
  }

  async function addCostLine(kind) {
    openCostModal(kind);
  }

  async function markAssessed() {
    setSaving(true);
    try {
      await sb.from('architecture_options').update({ status: 'assessed' }).eq('id', optionId);
      await load();
      showToast('Option marked as assessed', 'success');
    } catch (e) {
      showToast(e.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Loader text="Loading option workspace…" />;
  if (!option) return null;

  const tco = costData ? calcOptionTCO({
    oneTime: costData.oneTime,
    recurringTotal: costData.recurring?.fiveYearTotal,
    effortCost: romCalc?.expected,
  }) : null;

  return (
    <InitiationLayout
      title={option.name}
      subtitle={`Solution option · ${OPTION_STATUS_LABEL[option.status]}`}
      badge={<Badge variant={option.status === 'selected' ? 'gr' : 'pu'}>{summary.score != null ? `${summary.score}% OOTB` : '—'}</Badge>}
      actions={
        <>
          <button type="button" className="btn-ghost" onClick={() => navigate(`/project/${project.id}/initiation/solution-options`)}>
            ← All options
          </button>
          {canWrite && option.status === 'in_assessment' && (
            <button type="button" className="btn-primary" disabled={saving} onClick={markAssessed}>Mark assessed</button>
          )}
        </>
      }
    >
      <WorkspaceTabs active={activeTab} onChange={setActiveTab} multiPlatform={multiPlatform} />

      {activeTab === 'platforms' && (
        <div className="sa-card">
          <div className="sa-card-hdr"><span className="sa-card-title">Platform mapping</span></div>
          <div className="sa-card-body">
            <p style={{ fontSize: 11, color: '#888780', marginBottom: 10 }}>
              List platforms in this option (comma-separated). Multiple platforms enable the integrations tab.
            </p>
            <textarea
              className="form-input"
              rows={3}
              disabled={readOnly}
              value={platformInput}
              onChange={(e) => setPlatformInput(e.target.value)}
            />
            {!readOnly && (
              <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
                <button type="button" className="btn-primary" disabled={saving} onClick={savePlatforms}>Save platforms</button>
              </div>
            )}
            {candidates.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div className="sa-card-title" style={{ marginBottom: 8 }}>Session candidates</div>
                <div className="platform-grid">
                  {candidates.map((c) => (
                    <div key={c.id} className="platform-card" style={{ cursor: 'default' }}>
                      <div className="platform-card-name">{c.name}</div>
                      <div className="platform-card-vendor">{c.vendor || c.solution_domains?.name || ''}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'fitgap' && (
        <div className="sa-card">
          <div className="sa-card-hdr">
            <span className="sa-card-title">Fit-gap assessment</span>
            <span className="sa-card-stat">{summary.assessed} / {requirements.length} assessed</span>
          </div>
          <div className="req-table-wrap">
            <table className="req-table">
              <thead>
                <tr>
                  <th>Ref</th>
                  <th>Requirement</th>
                  <th>OOTB tier</th>
                  <th>Size</th>
                </tr>
              </thead>
              <tbody>
                {requirements.map((r) => {
                  const a = assessments.get(r.id) || {};
                  return (
                    <tr key={r.id}>
                      <td className="req-ref">{r.ref}</td>
                      <td><div className="req-title">{r.title}</div></td>
                      <td>
                        <select
                          className="req-inline-select"
                          disabled={readOnly || saving}
                          value={a.ootb_classification || ''}
                          onChange={(e) => updateAssessment(r.id, { ootb_classification: e.target.value || null })}
                        >
                          <option value="">—</option>
                          {Object.entries(OOTB_CLASSIFICATION_LABEL).map(([k, v]) => (
                            <option key={k} value={k}>{v}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          className="req-inline-select"
                          disabled={readOnly || saving}
                          value={a.t_shirt_size || ''}
                          onChange={(e) => updateAssessment(r.id, { t_shirt_size: e.target.value || null })}
                        >
                          <option value="">—</option>
                          {T_SHIRT_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'integrations' && multiPlatform && (
        <div className="sa-card">
          <div className="sa-card-hdr">
            <span className="sa-card-title">Integration assessment</span>
            <button type="button" className="btn-ghost" disabled={readOnly} onClick={() => setShowAddInterface(true)}>Add interface</button>
          </div>
          <div className="sa-card-body">
            {interfaces.length === 0 ? (
              <p style={{ color: '#888780', fontSize: 12 }}>No interfaces for this option yet.</p>
            ) : (
              <table className="req-table">
                <thead>
                  <tr><th>Ref</th><th>Name</th><th>Route</th><th>Type</th></tr>
                </thead>
                <tbody>
                  {interfaces.map((i) => (
                    <tr key={i.id}>
                      <td className="req-ref">{i.ref}</td>
                      <td>{i.name}</td>
                      <td style={{ fontSize: 11 }}>{i.source_system} → {i.target_system}</td>
                      <td>{INTERFACE_TYPE_LABEL[i.interface_type] || i.interface_type}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {activeTab === 'architecture' && (
        <div className="sa-card">
          <div className="sa-card-hdr"><span className="sa-card-title">Architecture overlay</span></div>
          <div className="sa-card-body">
            {ARCHITECTURE_NARRATIVE_SECTIONS.map((sec) => (
              <div key={sec.key} className="narrative-section">
                <div className="narrative-label">{sec.label}</div>
                <textarea
                  className="form-input"
                  rows={3}
                  disabled={readOnly}
                  value={narrative[sec.key] || ''}
                  onChange={(e) => saveNarrative(sec.key, e.target.value)}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'rom' && costData && (
        <>
          <div className="m4">
            <div className="met">
              <div className="mlbl">Effort ROM</div>
              <div className="mval">{formatCurrency(romCalc?.expected)}</div>
              <div className="msub">PERT expected</div>
            </div>
            <div className="met">
              <div className="mlbl">One-time</div>
              <div className="mval">{formatCurrency(costData.oneTime.total)}</div>
              <div className="msub">CapEx {formatCurrency(costData.oneTime.capex)} · OpEx {formatCurrency(costData.oneTime.opex)}</div>
            </div>
            <div className="met">
              <div className="mlbl">5-yr recurring</div>
              <div className="mval">{formatCurrency(costData.recurring.fiveYearTotal)}</div>
              <div className="msub">{costData.escalation}% escalation default</div>
            </div>
            <div className="met">
              <div className="mlbl">TCO (cash)</div>
              <div className="mval">{formatCurrency(tco)}</div>
              <div className="msub">effort + one-time + recurring</div>
            </div>
          </div>

          <p className="gate-banner info" style={{ marginBottom: 12 }}>
            CapEx/OpEx tags are informational only — no depreciation calculation is performed in-app.
          </p>

          <div className="sa-card">
            <div className="sa-card-hdr">
              <span className="sa-card-title">Recurring cost lines</span>
              {!readOnly && <button type="button" className="btn-ghost" onClick={() => addCostLine('recurring')}>Add line</button>}
            </div>
            <div className="sa-card-body">
              {costData.recurringLines.length === 0 ? (
                <p style={{ fontSize: 12, color: '#888780' }}>No recurring costs yet.</p>
              ) : (
                costData.recurringLines.map((line) => (
                  <div key={line.id} className="so-cost-row">
                    <span>{line.cost_types?.name || line.description}</span>
                    <span>{line.quantity} × {formatCurrency(line.unit_rate)} / {BILLING_FREQUENCY_LABEL[line.billing_frequency]}</span>
                    <span>
                      Yr {line.start_year}–{line.end_year}
                      {!readOnly && (
                        <>
                          <button type="button" className="btn-ghost" style={{ fontSize: 10, marginLeft: 6 }} onClick={() => openCostModal('recurring', line)}>Edit</button>
                          <button type="button" className="btn-ghost" style={{ fontSize: 10 }} onClick={() => deleteCostLine('recurring', line.id)}>Delete</button>
                        </>
                      )}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="sa-card">
            <div className="sa-card-hdr">
              <span className="sa-card-title">One-time cost lines</span>
              {!readOnly && <button type="button" className="btn-ghost" onClick={() => addCostLine('one_time')}>Add line</button>}
            </div>
            <div className="sa-card-body">
              {costData.oneTimeLines.map((line) => (
                <div key={line.id} className="so-cost-row">
                  <span>{line.cost_types?.name}</span>
                  <span>{formatCurrency(line.amount)}</span>
                  <span>
                    {CAPEX_OPEX_LABEL[line.capex_opex]}
                    {!readOnly && (
                      <>
                        <button type="button" className="btn-ghost" style={{ fontSize: 10, marginLeft: 6 }} onClick={() => openCostModal('one_time', line)}>Edit</button>
                        <button type="button" className="btn-ghost" style={{ fontSize: 10 }} onClick={() => deleteCostLine('one_time', line.id)}>Delete</button>
                      </>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="sa-card">
            <div className="sa-card-hdr"><span className="sa-card-title">Recurring by category (year 1)</span></div>
            <div className="sa-card-body">
              {Object.entries(costData.recurring.byYearCategory[1] || {}).map(([cat, amt]) => (
                <div key={cat} className="so-cost-row">
                  <span>{COST_CATEGORY_LABEL[cat] || cat}</span>
                  <span>{formatCurrency(amt)}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {showAddInterface && (
        <div className="modal-overlay" onClick={() => setShowAddInterface(false)} role="presentation">
          <div className="modal-card" onClick={(e) => e.stopPropagation()} role="dialog">
            <div className="modal-hdr">New interface</div>
            <div className="form-row">
              <label className="form-label">Name</label>
              <input className="form-input" value={newInterface.name} onChange={(e) => setNewInterface((p) => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="form-2col">
              <div className="form-row">
                <label className="form-label">Source</label>
                <input className="form-input" value={newInterface.source_system} onChange={(e) => setNewInterface((p) => ({ ...p, source_system: e.target.value }))} />
              </div>
              <div className="form-row">
                <label className="form-label">Target</label>
                <input className="form-input" value={newInterface.target_system} onChange={(e) => setNewInterface((p) => ({ ...p, target_system: e.target.value }))} />
              </div>
            </div>
            <div className="modal-foot">
              <button type="button" className="btn-ghost" onClick={() => setShowAddInterface(false)}>Cancel</button>
              <button type="button" className="btn-primary" disabled={saving} onClick={addInterface}>Add</button>
            </div>
          </div>
        </div>
      )}

      {costModal && (
        <div className="modal-overlay" onClick={() => setCostModal(null)} role="presentation">
          <div className="modal-card" onClick={(e) => e.stopPropagation()} role="dialog">
            <div className="modal-hdr">{costModal.form.id ? 'Edit' : 'Add'} {costModal.kind === 'recurring' ? 'recurring' : 'one-time'} cost</div>
            <div className="form-row">
              <label className="form-label">Cost type</label>
              <select className="form-select" value={costModal.form.cost_type_id || ''}
                onChange={(e) => {
                  const ct = costTypes.find((t) => t.id === e.target.value);
                  setCostModal((m) => ({
                    ...m,
                    form: {
                      ...m.form,
                      cost_type_id: e.target.value,
                      unit_rate: ct?.default_rate ?? m.form.unit_rate,
                      amount: ct?.default_rate ?? m.form.amount,
                      billing_frequency: ct?.default_billing_frequency || m.form.billing_frequency,
                    },
                  }));
                }}>
                {costTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            {costModal.kind === 'recurring' ? (
              <>
                <div className="form-2col">
                  <div className="form-row">
                    <label className="form-label">Quantity</label>
                    <input className="form-input" type="number" value={costModal.form.quantity}
                      onChange={(e) => setCostModal((m) => ({ ...m, form: { ...m.form, quantity: e.target.value } }))} />
                  </div>
                  <div className="form-row">
                    <label className="form-label">Unit rate</label>
                    <input className="form-input" type="number" value={costModal.form.unit_rate}
                      onChange={(e) => setCostModal((m) => ({ ...m, form: { ...m.form, unit_rate: e.target.value } }))} />
                  </div>
                </div>
                <div className="form-2col">
                  <div className="form-row">
                    <label className="form-label">Frequency</label>
                    <select className="form-select" value={costModal.form.billing_frequency}
                      onChange={(e) => setCostModal((m) => ({ ...m, form: { ...m.form, billing_frequency: e.target.value } }))}>
                      {Object.entries(BILLING_FREQUENCY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  <div className="form-row">
                    <label className="form-label">Escalation % (optional)</label>
                    <input className="form-input" type="number" step="0.1" value={costModal.form.escalation_pct}
                      onChange={(e) => setCostModal((m) => ({ ...m, form: { ...m.form, escalation_pct: e.target.value } }))} />
                  </div>
                </div>
                <div className="form-2col">
                  <div className="form-row">
                    <label className="form-label">Start year</label>
                    <input className="form-input" type="number" min={1} max={5} value={costModal.form.start_year}
                      onChange={(e) => setCostModal((m) => ({ ...m, form: { ...m.form, start_year: e.target.value } }))} />
                  </div>
                  <div className="form-row">
                    <label className="form-label">End year</label>
                    <input className="form-input" type="number" min={1} max={5} value={costModal.form.end_year}
                      onChange={(e) => setCostModal((m) => ({ ...m, form: { ...m.form, end_year: e.target.value } }))} />
                  </div>
                </div>
              </>
            ) : (
              <div className="form-2col">
                <div className="form-row">
                  <label className="form-label">Amount</label>
                  <input className="form-input" type="number" value={costModal.form.amount}
                    onChange={(e) => setCostModal((m) => ({ ...m, form: { ...m.form, amount: e.target.value } }))} />
                </div>
                <div className="form-row">
                  <label className="form-label">CapEx / OpEx</label>
                  <select className="form-select" value={costModal.form.capex_opex}
                    onChange={(e) => setCostModal((m) => ({ ...m, form: { ...m.form, capex_opex: e.target.value } }))}>
                    {Object.entries(CAPEX_OPEX_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
              </div>
            )}
            <div className="modal-foot">
              <button type="button" className="btn-ghost" onClick={() => setCostModal(null)}>Cancel</button>
              <button type="button" className="btn-primary" disabled={saving} onClick={() => saveCostLine(costModal.form)}>Save</button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={toast.type === 'success' ? 'toast-success' : 'toast-error'}>{toast.msg}</div>
      )}
    </InitiationLayout>
  );
}
